export type AssetMemoryPressurePage = {
    key: string;
    priority: number;
    loaded: boolean;
    eligible: boolean;
    memory: number;
};

export type AssetMemoryPressurePlan = {
    evictPageKeys: string[];
    memoryPressureHit: boolean;
    retainedPriorityFrontier: number | null;
};

/**
 * Reconciles an already-over-budget asset residency set to the same ordered
 * prefix produced when the pages are loaded from an empty scene. The first
 * page that crosses the limit is retained; every lower-priority resident page
 * is removed from that point onward.
 */
export function planAssetMemoryPressureResidency(
    currentMemory: number,
    memoryLimit: number,
    visiblePages: AssetMemoryPressurePage[],
    hiddenPages: AssetMemoryPressurePage[]
): AssetMemoryPressurePlan {
    if (currentMemory <= memoryLimit) {
        return { evictPageKeys: [], memoryPressureHit: false, retainedPriorityFrontier: null };
    }

    const evictPageKeys: string[] = [];
    let plannedMemory = currentMemory;

    // A blank load at this camera would not contain pages outside the frustum
    // or pages that are no longer eligible for asset display.
    for (const page of hiddenPages) {
        if (!page.loaded) continue;
        evictPageKeys.push(page.key);
        plannedMemory -= page.memory;
    }

    for (const page of visiblePages) {
        if (!page.loaded || page.eligible) continue;
        evictPageKeys.push(page.key);
        plannedMemory -= page.memory;
    }

    if (plannedMemory <= memoryLimit) {
        return { evictPageKeys, memoryPressureHit: false, retainedPriorityFrontier: null };
    }

    const eligiblePages = visiblePages.filter(page => page.eligible);
    let completedPrefixMemory = 0;
    let firstIncompletePriority: number | null = null;
    let retainedPriorityFrontier: number | null = null;

    for (const page of eligiblePages) {
        if (!page.loaded) {
            firstIncompletePriority = page.priority;
            break;
        }

        completedPrefixMemory += page.memory;
        if (completedPrefixMemory > memoryLimit) {
            retainedPriorityFrontier = page.priority;
            break;
        }
    }

    if (retainedPriorityFrontier != null) {
        for (const page of visiblePages) {
            if (page.loaded && page.priority > retainedPriorityFrontier) {
                evictPageKeys.push(page.key);
            }
        }

        return {
            evictPageKeys: Array.from(new Set(evictPageKeys)),
            memoryPressureHit: eligiblePages.some(page => page.priority > retainedPriorityFrontier),
            retainedPriorityFrontier,
        };
    }

    if (firstIncompletePriority != null) {
        const lowerPriorityLoadedPages = visiblePages
            .filter(page => page.loaded && page.priority > firstIncompletePriority)
            .sort((left, right) => right.priority - left.priority);

        for (const page of lowerPriorityLoadedPages) {
            if (plannedMemory <= memoryLimit) break;
            evictPageKeys.push(page.key);
            plannedMemory -= page.memory;
        }
    }

    return {
        evictPageKeys: Array.from(new Set(evictPageKeys)),
        memoryPressureHit: plannedMemory > memoryLimit,
        retainedPriorityFrontier: null,
    };
}

/**
 * Preserves the existing admission rule: a page may be accepted whenever the
 * scene is below the cap, even if that page itself crosses the cap. When the
 * scene is already at the cap, only strictly lower-priority resident pages may
 * be replaced, and the replacement is atomic.
 */
export function planAssetPageAdmission(
    currentMemory: number,
    memoryLimit: number,
    incomingPriority: number,
    visiblePages: AssetMemoryPressurePage[],
    hiddenPages: AssetMemoryPressurePage[]
): { allowed: boolean; evictPageKeys: string[]; } {
    if (currentMemory < memoryLimit) {
        return { allowed: true, evictPageKeys: [] };
    }

    let plannedMemory = currentMemory;
    const candidates = [
        ...hiddenPages.filter(page => page.loaded),
        ...visiblePages
            .filter(page => page.loaded && page.priority > incomingPriority)
            .sort((left, right) => right.priority - left.priority),
    ];
    const evictPageKeys: string[] = [];

    for (const page of candidates) {
        if (plannedMemory < memoryLimit) break;
        evictPageKeys.push(page.key);
        plannedMemory -= page.memory;
    }

    if (plannedMemory >= memoryLimit) {
        return { allowed: false, evictPageKeys: [] };
    }

    return { allowed: true, evictPageKeys: Array.from(new Set(evictPageKeys)) };
}
